import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { exportarExcel, exportarPDF } from '../utils/exportar';
import { buildOpciones, opcionDefault, precioReferencial } from '../utils/presentaciones';
import '../styles/Cotizaciones.css';

const fmt2 = (n) => (Number(n) || 0).toFixed(2);
const fmtFecha = (d) => {
  if (!d) return '—';
  const s = d instanceof Date ? d.toISOString().slice(0, 10) : String(d).slice(0, 10);
  const [y, m, dia] = s.split('-');
  return `${dia}/${m}/${y}`;
};
const hoyISO = () => new Date().toISOString().slice(0, 10);

/* Iconos inline (SVG autocontenidos — react-icons no renderiza en algunos equipos) */
const svgProps = (size) => ({
  xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 24 24',
  width: size, height: size, fill: 'none', stroke: 'currentColor',
  strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round',
  style: { display: 'block' },
});
const IconPrint = ({ size = 16 }) => (
  <svg {...svgProps(size)}><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
);
const IconSlash = ({ size = 16 }) => (
  <svg {...svgProps(size)}><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
);
const IconEye = ({ size = 16 }) => (
  <svg {...svgProps(size)}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
);
const IconSearch = ({ size = 16 }) => (
  <svg {...svgProps(size)}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
);
const IconX = ({ size = 16 }) => (
  <svg {...svgProps(size)}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
);
const IconPlus = ({ size = 16 }) => (
  <svg {...svgProps(size)}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
);
const IconArrowLeft = ({ size = 16 }) => (
  <svg {...svgProps(size)}><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
);
const IconRefresh = ({ size = 16 }) => (
  <svg {...svgProps(size)}><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
);
const IconFile = ({ size = 18 }) => (
  <svg {...svgProps(size)}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>
);
const IconDownload = ({ size = 14 }) => (
  <svg {...svgProps(size)}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
);

/* Etiqueta corta de almacén — misma lógica que Ventas */
const almacenTag = (nombre) => {
  if (!nombre) return '?';
  const words = nombre.trim().toUpperCase().split(/[\s_]+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 4);
  return words.map(w => w[0]).join('').slice(0, 3);
};

const ESTADO_BADGE = {
  VIGENTE:  'cot-badge-vigente',
  ANULADA:  'cot-badge-anulada',
  VENDIDA:  'cot-badge-vendida',
};

export default function CotizacionesPage() {
  const [vista, setVista] = useState('lista'); // lista | nueva

  return vista === 'nueva'
    ? <NuevaCotizacion onVolver={() => setVista('lista')} />
    : <ListaCotizaciones onNueva={() => setVista('nueva')} />;
}

/* ═══════════════ LISTADO ═══════════════ */
function ListaCotizaciones({ onNueva }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [desde, setDesde] = useState(hoyISO().slice(0, 8) + '01');
  const [hasta, setHasta] = useState(hoyISO());
  const [estado, setEstado] = useState('');
  const [search, setSearch] = useState('');
  const [detalle, setDetalle] = useState(null);   // cotización expandida
  const [confirmAnular, setConfirmAnular] = useState(null);
  const [msg, setMsg] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { desde, hasta };
      if (estado) params.estado = estado;
      if (search.trim()) params.search = search.trim();
      const r = await api.get('/api/cotizaciones', { params });
      setRows(r.data?.data || []);
    } catch (e) {
      setMsg({ tipo: 'error', texto: 'Error al cargar cotizaciones' });
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, estado, search]);

  useEffect(() => { cargar(); }, [cargar]);

  const verDetalle = async (id) => {
    try {
      const r = await api.get(`/api/cotizaciones/${id}`);
      setDetalle(r.data?.data || null);
    } catch (e) {
      setMsg({ tipo: 'error', texto: 'No se pudo cargar el detalle' });
    }
  };

  const anular = async () => {
    if (!confirmAnular) return;
    try {
      await api.delete(`/api/cotizaciones/${confirmAnular.id}/anular`);
      setMsg({ tipo: 'ok', texto: `Cotización ${confirmAnular.numero} anulada` });
      setConfirmAnular(null);
      cargar();
    } catch (e) {
      setMsg({ tipo: 'error', texto: e.response?.data?.message || 'Error al anular' });
      setConfirmAnular(null);
    }
  };

  const imprimir = (id) => window.open(`/print-cotizacion/${id}`, '_blank');

  /* Exportación PDF / Excel del listado filtrado */
  const exportar = (tipo) => {
    if (!rows.length) { setMsg({ tipo: 'error', texto: 'No hay cotizaciones para exportar' }); return; }
    const encabezados = ['Número', 'Fecha', 'Vence', 'Cliente', 'Ítems', 'Total S/', 'Estado', 'Usuario'];
    const base = rows.map((c) => [
      c.numero,
      fmtFecha(c.fecha),
      fmtFecha(c.fecha_vencimiento),
      c.cliente_nombre || 'Cliente varios',
      Number(c.nro_items || 0),
      Number(c.total || 0),
      c.estado,
      c.usuario_nombre || '—',
    ]);
    const nombre = `cotizaciones_${desde}_${hasta}`;
    if (tipo === 'pdf') {
      exportarPDF(
        'Cotizaciones', nombre, encabezados,
        base.map((f) => f.map((v, i) => (i === 5 ? fmt2(v) : v))),
        `Del ${fmtFecha(desde)} al ${fmtFecha(hasta)}${estado ? ` · Estado: ${estado}` : ''}`,
      );
    } else {
      exportarExcel(nombre, encabezados, base);
    }
  };

  return (
    <div className="cot-page">
      <div className="cot-header">
        <h2><span className="cot-h-icon"><IconFile /></span> Cotizaciones</h2>
        <button className="cot-btn-primary" onClick={onNueva}><IconPlus /> Nueva Cotización</button>
      </div>

      {msg && (
        <div className={`cot-msg ${msg.tipo === 'ok' ? 'cot-msg-ok' : 'cot-msg-error'}`} onClick={() => setMsg(null)}>
          {msg.texto}
        </div>
      )}

      <div className="cot-filtros">
        <label>Desde <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
        <label>Hasta <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
        <label>Estado
          <select value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="">Todos</option>
            <option value="VIGENTE">Vigente</option>
            <option value="ANULADA">Anulada</option>
            <option value="VENDIDA">Vendida</option>
          </select>
        </label>
        <div className="cot-search">
          <IconSearch size={15} />
          <input
            placeholder="N° o cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && cargar()}
          />
        </div>
        <button className="cot-btn-ghost" onClick={cargar}><IconRefresh size={14} /> Actualizar</button>
        <button className="cot-btn-ghost" onClick={() => exportar('pdf')} title="Exportar listado a PDF">
          <IconDownload /> PDF
        </button>
        <button className="cot-btn-ghost" onClick={() => exportar('excel')} title="Exportar listado a Excel">
          <IconDownload /> Excel
        </button>
      </div>

      <div className="cot-tabla-wrap">
        <table className="cot-tabla">
          <thead>
            <tr>
              <th>Número</th><th>Fecha</th><th>Vence</th><th>Cliente</th>
              <th className="cot-num">Ítems</th><th className="cot-num">Total</th>
              <th>Estado</th><th>Usuario</th><th className="cot-acciones-col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="cot-empty">Cargando...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="cot-empty">Sin cotizaciones en el rango</td></tr>
            ) : rows.map((c) => (
              <tr key={c.id} className={c.estado === 'ANULADA' ? 'cot-row-anulada' : ''}>
                <td className="cot-numero">{c.numero}</td>
                <td>{fmtFecha(c.fecha)}</td>
                <td>{fmtFecha(c.fecha_vencimiento)}</td>
                <td>{c.cliente_nombre || 'Cliente varios'}</td>
                <td className="cot-num">{c.nro_items}</td>
                <td className="cot-num">S/ {fmt2(c.total)}</td>
                <td><span className={`cot-badge ${ESTADO_BADGE[c.estado] || ''}`}>{c.estado}</span></td>
                <td>{c.usuario_nombre || '—'}</td>
                <td className="cot-acciones">
                  <button className="cot-icon-btn cot-icon-ver" title="Ver detalle" onClick={() => verDetalle(c.id)}><IconEye /></button>
                  <button className="cot-icon-btn cot-icon-print" title="Imprimir A4" onClick={() => imprimir(c.id)}><IconPrint /></button>
                  {c.estado === 'VIGENTE' && (
                    <button className="cot-icon-btn cot-icon-anular" title="Anular" onClick={() => setConfirmAnular(c)}><IconSlash /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detalle && (
        <div className="cot-modal-overlay">
          <div className="cot-modal">
            <div className="cot-modal-head">
              <h3>{detalle.numero}</h3>
              <button className="cot-icon-btn" onClick={() => setDetalle(null)}><IconX /></button>
            </div>
            <div className="cot-modal-body">
              <div className="cot-det-grid">
                <div><b>Cliente:</b> {detalle.cliente_nombre || 'Cliente varios'}</div>
                <div><b>Fecha:</b> {fmtFecha(detalle.fecha)}</div>
                <div><b>Vence:</b> {fmtFecha(detalle.fecha_vencimiento)} ({detalle.dias_vigencia} días)</div>
                <div><b>Estado:</b> <span className={`cot-badge ${ESTADO_BADGE[detalle.estado] || ''}`}>{detalle.estado}</span></div>
                {detalle.detalle && <div className="cot-det-full"><b>Detalle:</b> {detalle.detalle}</div>}
              </div>
              <table className="cot-tabla cot-tabla-mini">
                <thead>
                  <tr><th>Código</th><th>Producto</th><th>Und</th><th className="cot-num">Cant.</th><th className="cot-num">P. Unit</th><th className="cot-num">Subtotal</th></tr>
                </thead>
                <tbody>
                  {(detalle.lineas || []).map((l) => (
                    <tr key={l.id}>
                      <td>{l.codigo}</td>
                      <td>{l.producto_nombre}</td>
                      <td>{l.unidad_short || '—'}</td>
                      <td className="cot-num">{Number(l.cantidad)}</td>
                      <td className="cot-num">{fmt2(l.precio_unitario)}</td>
                      <td className="cot-num">{fmt2(l.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="cot-tot-box">
                <div><span>Subtotal</span><span>S/ {fmt2(detalle.subtotal)}</span></div>
                {Number(detalle.descuento) > 0 && <div><span>Descuento</span><span>- S/ {fmt2(detalle.descuento)}</span></div>}
                {Number(detalle.igv) > 0 && <div><span>IGV (18%)</span><span>S/ {fmt2(detalle.igv)}</span></div>}
                <div className="cot-tot-final"><span>Total</span><span>S/ {fmt2(detalle.total)}</span></div>
              </div>
            </div>
            <div className="cot-modal-foot">
              <button className="cot-btn-ghost" onClick={() => setDetalle(null)}>Cerrar</button>
              <button className="cot-btn-primary" onClick={() => imprimir(detalle.id)}><IconPrint /> Imprimir A4</button>
            </div>
          </div>
        </div>
      )}

      {confirmAnular && (
        <div className="cot-modal-overlay">
          <div className="cot-modal cot-modal-sm">
            <div className="cot-confirm">
              <IconSlash size={30} />
              <p>¿Anular la cotización <b>{confirmAnular.numero}</b>?</p>
              <p className="cot-confirm-sub">No afecta stock ni caja. Solo cambia su estado.</p>
              <div className="cot-modal-foot">
                <button className="cot-btn-ghost" onClick={() => setConfirmAnular(null)}>Cancelar</button>
                <button className="cot-btn-danger" onClick={anular}>Sí, anular</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════ NUEVA COTIZACIÓN ═══════════════ */
function NuevaCotizacion({ onVolver }) {
  const [fecha, setFecha] = useState(hoyISO());
  const [dias, setDias] = useState(3);
  const [detalleTxt, setDetalleTxt] = useState('');
  const [incluirIgv, setIncluirIgv] = useState(true);
  const [descuento, setDescuento] = useState('');

  // Cliente
  const [cliente, setCliente] = useState(null);
  const [cliSearch, setCliSearch] = useState('');
  const [cliResults, setCliResults] = useState([]);
  const cliTimer = useRef(null);

  // Productos
  const [prodSearch, setProdSearch] = useState('');
  const [prodResults, setProdResults] = useState([]);
  const prodTimer = useRef(null);
  const [carrito, setCarrito] = useState([]);

  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState(null);

  /* búsqueda cliente (300ms debounce, min 2 chars) */
  useEffect(() => {
    clearTimeout(cliTimer.current);
    if (cliSearch.trim().length < 2) { setCliResults([]); return; }
    cliTimer.current = setTimeout(async () => {
      try {
        const r = await api.get('/api/clientes', { params: { search: cliSearch.trim() } });
        setCliResults(r.data?.data || []);
      } catch (_) { setCliResults([]); }
    }, 300);
    return () => clearTimeout(cliTimer.current);
  }, [cliSearch]);

  /* búsqueda producto — endpoint propio de cotizaciones (fila por almacén con stock) */
  useEffect(() => {
    clearTimeout(prodTimer.current);
    if (prodSearch.trim().length < 2) { setProdResults([]); return; }
    prodTimer.current = setTimeout(async () => {
      try {
        const r = await api.get('/api/cotizaciones/productos-busqueda', { params: { q: prodSearch.trim() } });
        setProdResults(r.data?.data || []);
      } catch (_) { setProdResults([]); }
    }, 300);
    return () => clearTimeout(prodTimer.current);
  }, [prodSearch]);

  const nombreCliente = (c) =>
    `${c.nombres || ''} ${c.apellido_paterno || ''} ${c.apellido_materno || ''}`.trim();

  const cartKey = (producto_id, almacen_id) => `${producto_id}_${almacen_id || 0}`;

  const agregarProducto = (p) => {
    const key      = cartKey(p.producto_id, p.almacen_id);
    const opciones = buildOpciones(p);
    const opcion   = opcionDefault(opciones);
    setCarrito((prev) => {
      const ya = prev.find((l) => l._key === key);
      if (ya) return prev.map((l) => l._key === key ? { ...l, cantidad: Number(l.cantidad) + 1 } : l);
      return [...prev, {
        _key: key,
        producto_id: p.producto_id,
        codigo: p.codigo,
        nombre: p.nombre,
        almacen_id: p.almacen_id || null,
        almacen_nombre: p.almacen_nombre || null,
        almacen_tag: almacenTag(p.almacen_nombre),
        stock: Number(p.stock) || 0,
        opciones,
        opcionKey: opcion.key,
        factor: opcion.factor,
        unidad_nombre: opcion.nombre,
        custom: false,
        cantidad: 1,
        precio_unitario: opcion.precio,
      }];
    });
    setProdSearch('');
    setProdResults([]);
  };

  const setLinea = (idx, campo, valor) =>
    setCarrito((prev) => prev.map((l, i) => (i === idx ? { ...l, [campo]: valor } : l)));

  const setPrecioManual = (idx, valor) =>
    setCarrito((prev) => prev.map((l, i) =>
      i === idx ? { ...l, precio_unitario: valor, custom: true } : l));

  const handleOpcion = (idx, opcionKey) =>
    setCarrito((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      const opcion = l.opciones.find((o) => o.key === opcionKey);
      if (!opcion) return l;
      return { ...l, opcionKey: opcion.key, factor: opcion.factor, unidad_nombre: opcion.nombre, precio_unitario: opcion.precio, custom: false };
    }));

  const quitarLinea = (idx) => setCarrito((prev) => prev.filter((_, i) => i !== idx));

  /* totales — misma matemática que Ventas */
  const subtotal = carrito.reduce((acc, l) =>
    acc + Math.round(((Number(l.cantidad) || 0) * (Number(l.precio_unitario) || 0) + Number.EPSILON) * 100) / 100, 0);
  const desc = Math.min(Number(descuento) || 0, subtotal);
  const baseIgv = subtotal - desc;
  const igv = incluirIgv ? Math.round((baseIgv * 0.18 + Number.EPSILON) * 100) / 100 : 0;
  const total = Math.round((baseIgv + igv + Number.EPSILON) * 100) / 100;

  const guardar = async () => {
    if (!carrito.length) { setMsg({ tipo: 'error', texto: 'Agrega al menos un producto' }); return; }
    if (carrito.some((l) => !(Number(l.cantidad) > 0))) { setMsg({ tipo: 'error', texto: 'Hay cantidades inválidas' }); return; }
    setGuardando(true);
    try {
      const r = await api.post('/api/cotizaciones', {
        cliente_id: cliente?.id || null,
        fecha,
        dias_vigencia: dias,
        detalle: detalleTxt.trim() || null,
        incluir_igv: incluirIgv,
        descuento: desc,
        lineas: carrito.map((l) => {
          const factor = Number(l.factor) || 1;
          return {
            producto_id: l.producto_id,
            // cantidad/precio_unitario en Unidad Mínima, igual que en Ventas
            cantidad: Math.round(Number(l.cantidad) * factor * 10000) / 10000,
            precio_unitario: Math.round((Number(l.precio_unitario) / factor) * 10000) / 10000,
            cantidad_presentacion: Number(l.cantidad),
            precio_unitario_presentacion: Number(l.precio_unitario),
            factor,
            unidad_nombre: l.unidad_nombre,
          };
        }),
      });
      const nueva = r.data?.data;
      setMsg({ tipo: 'ok', texto: `Cotización ${nueva?.numero} registrada` });
      if (nueva?.id) window.open(`/print-cotizacion/${nueva.id}`, '_blank');
      setTimeout(onVolver, 900);
    } catch (e) {
      setMsg({ tipo: 'error', texto: e.response?.data?.message || 'Error al guardar' });
      setGuardando(false);
    }
  };

  return (
    <div className="cot-page">
      <div className="cot-header">
        <h2><span className="cot-h-icon"><IconFile /></span> Nueva Cotización</h2>
        <button className="cot-btn-ghost" onClick={onVolver}><IconArrowLeft size={14} /> Volver al listado</button>
      </div>

      {msg && (
        <div className={`cot-msg ${msg.tipo === 'ok' ? 'cot-msg-ok' : 'cot-msg-error'}`} onClick={() => setMsg(null)}>
          {msg.texto}
        </div>
      )}

      <div className="cot-form-grid">
        {/* Panel izquierdo: datos generales */}
        <div className="cot-card">
          <h4>Datos de la cotización</h4>
          <div className="cot-field-row">
            <label>Fecha <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} /></label>
            <label>Días de vigencia
              <input type="number" min="0" value={dias} onChange={(e) => setDias(e.target.value)} />
            </label>
          </div>

          <div className="cot-field">
            <span>Cliente</span>
            {cliente ? (
              <div className="cot-cliente-sel">
                <span>{nombreCliente(cliente)} — {cliente.nro_documento || 's/doc'}</span>
                <button className="cot-icon-btn" title="Quitar cliente" onClick={() => setCliente(null)}><IconX size={14} /></button>
              </div>
            ) : (
              <div className="cot-autocomplete">
                <input
                  placeholder="Buscar cliente (mín. 2 letras)..."
                  value={cliSearch}
                  onChange={(e) => setCliSearch(e.target.value)}
                />
                {cliResults.length > 0 && (
                  <ul className="cot-sugerencias">
                    {cliResults.map((c) => (
                      <li key={c.id} onClick={() => { setCliente(c); setCliSearch(''); setCliResults([]); }}>
                        {nombreCliente(c)} <small>{c.nro_documento}</small>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <label className="cot-field">Detalle / referencia
            <textarea
              rows={2}
              placeholder="Ej: cotización solicitada por obra av. Los Pinos"
              value={detalleTxt}
              onChange={(e) => setDetalleTxt(e.target.value)}
            />
          </label>

          <div className="cot-field-row">
            <label className="cot-check">
              <input type="checkbox" checked={incluirIgv} onChange={(e) => setIncluirIgv(e.target.checked)} />
              Incluir IGV (18%)
            </label>
            <label>Descuento S/
              <input type="number" min="0" step="0.01" value={descuento} onChange={(e) => setDescuento(e.target.value)} />
            </label>
          </div>

          <div className="cot-nota">
            Esta cotización <b>no descuenta stock</b> ni registra movimientos de caja.
          </div>
        </div>

        {/* Panel derecho: productos + totales */}
        <div className="cot-card">
          <h4>Productos</h4>
          <div className="cot-autocomplete">
            <div className="cot-search cot-search-full">
              <IconSearch size={15} />
              <input
                placeholder="Buscar producto por nombre o código..."
                value={prodSearch}
                onChange={(e) => setProdSearch(e.target.value)}
              />
            </div>
            {prodResults.length > 0 && (
              <ul className="cot-sugerencias">
                {prodResults.map((p) => {
                  const ref = precioReferencial(p);
                  return (
                    <li key={cartKey(p.producto_id, p.almacen_id)} onClick={() => agregarProducto(p)}>
                      <span className="cot-alm-tag" title={p.almacen_nombre}>{almacenTag(p.almacen_nombre)}</span>
                      <span className="cot-sug-nombre"><b>{p.codigo}</b> {p.nombre}</span>
                      <small className="cot-sug-info">
                        {Number(p.stock) > 0 ? `Stock: ${Number(p.stock)}` : 'Sin stock'} · S/ {fmt2(ref.precio)} / {ref.unidad}
                      </small>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <table className="cot-tabla cot-tabla-mini">
            <thead>
              <tr><th>Producto</th><th>Und</th><th className="cot-num">Cant.</th><th className="cot-num">P. Unit</th><th className="cot-num">Subtotal</th><th></th></tr>
            </thead>
            <tbody>
              {carrito.length === 0 ? (
                <tr><td colSpan={6} className="cot-empty">Agrega productos con el buscador</td></tr>
              ) : carrito.map((l, i) => (
                <tr key={l._key}>
                  <td>
                    {l.nombre}
                    <span className="cot-cart-alm-tag" title={l.almacen_nombre}>{l.almacen_tag}</span>
                  </td>
                  <td>{l.unidad_nombre || '—'}</td>
                  <td className="cot-num">
                    <input className="cot-input-mini" type="number" min="0.0001" step="any"
                      value={l.cantidad} onChange={(e) => setLinea(i, 'cantidad', e.target.value)} />
                  </td>
                  <td className="cot-num">
                    <div className="cot-precio-group">
                      <select
                        className="cot-precio-select"
                        value={l.custom ? '__custom__' : l.opcionKey}
                        onChange={(e) => { if (e.target.value !== '__custom__') handleOpcion(i, e.target.value); }}
                      >
                        {l.opciones.map((o) => (
                          <option key={o.key} value={o.key}>{o.nombre}{o.label ? ` (${o.label})` : ''}</option>
                        ))}
                        {l.custom && <option value="__custom__">Personalizado</option>}
                      </select>
                      <input className="cot-input-mini" type="number" min="0" step="0.01"
                        value={l.precio_unitario} onChange={(e) => setPrecioManual(i, e.target.value)} />
                    </div>
                  </td>
                  <td className="cot-num">{fmt2((Number(l.cantidad) || 0) * (Number(l.precio_unitario) || 0))}</td>
                  <td><button className="cot-icon-btn cot-icon-anular" title="Quitar" onClick={() => quitarLinea(i)}><IconX size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="cot-tot-box">
            <div><span>Subtotal</span><span>S/ {fmt2(subtotal)}</span></div>
            {desc > 0 && <div><span>Descuento</span><span>- S/ {fmt2(desc)}</span></div>}
            {incluirIgv && <div><span>IGV (18%)</span><span>S/ {fmt2(igv)}</span></div>}
            <div className="cot-tot-final"><span>Total</span><span>S/ {fmt2(total)}</span></div>
          </div>

          <button className="cot-btn-primary cot-btn-guardar" disabled={guardando || !carrito.length} onClick={guardar}>
            {guardando ? 'Guardando...' : 'Guardar e imprimir A4'}
          </button>
        </div>
      </div>
    </div>
  );
}
